import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import maplibregl, { Map as MapLibreMap, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { BatteryCharging, Filter, Plug, Zap, MapPin, Search } from 'lucide-react';
import { evApi, geocodingApi, type EvChargePoint, type ForwardGeocodingResult } from '../services/api';
import { useHasModule } from '../store/modules';
import { useBreakpoint } from '../hooks/useBreakpoint';

const DEFAULT_CENTER: [number, number] = [6.13, 49.61]; // Luxembourg city
const DEFAULT_ZOOM = 11;

// Common connection type IDs from OpenChargeMap (https://api.openchargemap.io/v3/referencedata/)
// Connector / power filter dictionaries. The "All" option uses an i18n key;
// brand names (CCS, Tesla, etc.) stay as-is since they are not translated.
type LabelMaybeKey = string | { key: string };
const CONNECTION_TYPES: Array<{ id: number; label: LabelMaybeKey }> = [
  { id: 0, label: { key: 'ev_chargers.filter_all' } },
  { id: 25, label: 'Type 2 (Mennekes)' },
  { id: 33, label: 'CCS Combo 2' },
  { id: 27, label: 'CHAdeMO' },
  { id: 1036, label: 'Tesla (Type 2)' },
  { id: 30, label: 'Tesla Supercharger' },
  { id: 28, label: 'Type 3' },
  { id: 32, label: 'Type 1 (J1772)' },
  { id: 8, label: 'CEE 7/4 Schuko' },
];

const POWER_PRESETS: Array<{ value: number; label: LabelMaybeKey }> = [
  { value: 0, label: { key: 'ev_chargers.filter_all' } },
  { value: 7, label: { key: 'ev_chargers.preset_7' } },
  { value: 22, label: { key: 'ev_chargers.preset_22' } },
  { value: 50, label: { key: 'ev_chargers.preset_50' } },
  { value: 150, label: { key: 'ev_chargers.preset_150' } },
];

function powerColor(kw: number | null): string {
  if (kw === null) return '#9ca3af';
  if (kw >= 150) return '#7c3aed'; // purple — HPC
  if (kw >= 50) return '#dc2626'; // red — fast DC
  if (kw >= 22) return '#ea580c'; // orange — T2 22kW
  if (kw >= 7) return '#10b981'; // green — slow AC
  return '#6b7280'; // grey
}

export default function EvChargingMapPage() {
  const { t } = useTranslation();
  // Helper: resolve a LabelMaybeKey to a string in the current language.
  const lbl = (l: LabelMaybeKey): string => (typeof l === 'string' ? l : t(l.key));
  const hasEvModule = useHasModule('EV');
  const { isMobile } = useBreakpoint();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markersRef = useRef<Marker[]>([]);
  const [points, setPoints] = useState<EvChargePoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minPowerKw, setMinPowerKw] = useState(0);
  const [connectionTypeId, setConnectionTypeId] = useState(0);
  const [selected, setSelected] = useState<EvChargePoint | null>(null);

  // Address search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ForwardGeocodingResult[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchMarkerRef = useRef<Marker | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const container = containerRef.current;
    mapRef.current = new maplibregl.Map({
      container,
      style: {
        version: 8,
        sources: {
          'raster-tiles': {
            type: 'raster',
            tiles: [
              'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
            ],
            tileSize: 256,
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          },
        },
        layers: [
          { id: 'raster-layer', type: 'raster', source: 'raster-tiles', minzoom: 0, maxzoom: 19 },
        ],
      },
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });
    mapRef.current.addControl(new maplibregl.NavigationControl(), 'top-right');
    mapRef.current.addControl(new maplibregl.AttributionControl({ compact: true }));
    mapRef.current.on('error', (e) => {
      // eslint-disable-next-line no-console
      console.error('[ev-map] MapLibre error', e?.error ?? e);
    });

    let timer: ReturnType<typeof setTimeout> | null = null;
    const onMoveEnd = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void fetchPoints(), 250);
    };
    mapRef.current.on('moveend', onMoveEnd);

    // First fetch once map is ready
    mapRef.current.on('load', () => void fetchPoints());

    // Force a layout pass after mount — common gotcha when the container
    // is created inside a flex parent that hasn't measured yet
    requestAnimationFrame(() => mapRef.current?.resize());

    // Keep the canvas in sync when the surrounding layout changes
    const ro = new ResizeObserver(() => mapRef.current?.resize());
    ro.observe(container);

    return () => {
      if (timer) clearTimeout(timer);
      ro.disconnect();
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when filters change
  useEffect(() => {
    if (mapRef.current?.loaded()) void fetchPoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minPowerKw, connectionTypeId]);

  // Debounced address search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const q = searchQuery.trim();
    if (q.length < 3) {
      setSearchResults([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        // Bias results toward the current map viewport (Nominatim 'viewbox' hint)
        let viewbox: [number, number, number, number] | undefined;
        if (mapRef.current) {
          const b = mapRef.current.getBounds();
          viewbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
        }
        const r = await geocodingApi.search(q, { limit: 6, viewbox });
        setSearchResults(r);
        setSearchOpen(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  function selectSearchResult(r: ForwardGeocodingResult) {
    if (!mapRef.current) return;
    setSearchQuery(r.displayName);
    setSearchOpen(false);

    // Fly to the result. If a bounding box is provided, fit it; else use zoom 14.
    if (r.boundingBox) {
      const [south, north, west, east] = r.boundingBox;
      mapRef.current.fitBounds(
        [
          [west, south],
          [east, north],
        ],
        { padding: 60, maxZoom: 16 },
      );
    } else {
      mapRef.current.flyTo({ center: [r.longitude, r.latitude], zoom: 14, speed: 1.4 });
    }

    // Drop a temporary search-result marker (replaces any prior one)
    searchMarkerRef.current?.remove();
    const el = document.createElement('div');
    el.style.cssText = `
      width: 22px; height: 22px; border-radius: 50%;
      background: #2563eb; border: 3px solid white;
      box-shadow: 0 0 6px rgba(0,0,0,0.5);
    `;
    el.title = r.displayName;
    searchMarkerRef.current = new maplibregl.Marker({ element: el })
      .setLngLat([r.longitude, r.latitude])
      .addTo(mapRef.current);
  }

  function clearSearch() {
    setSearchQuery('');
    setSearchResults([]);
    setSearchOpen(false);
    searchMarkerRef.current?.remove();
    searchMarkerRef.current = null;
  }

  async function fetchPoints() {
    if (!mapRef.current) return;
    const b = mapRef.current.getBounds();
    setLoading(true);
    setError(null);
    try {
      const r = await evApi.listChargePoints(
        {
          swLat: b.getSouth(),
          swLng: b.getWest(),
          neLat: b.getNorth(),
          neLng: b.getEast(),
        },
        {
          minPowerKw: minPowerKw > 0 ? minPowerKw : undefined,
          connectionTypeId: connectionTypeId > 0 ? connectionTypeId : undefined,
        },
      );
      setPoints(r);
    } catch (e) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (e instanceof Error ? e.message : 'Erreur');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  // Render markers when points change
  useEffect(() => {
    if (!mapRef.current) return;
    for (const m of markersRef.current) m.remove();
    markersRef.current = [];

    for (const p of points) {
      const el = document.createElement('div');
      el.className = 'ev-marker';
      const color = powerColor(p.maxPowerKw);
      el.style.cssText = `
        width: 14px; height: 14px; border-radius: 50%;
        background: ${color}; border: 2px solid white;
        box-shadow: 0 0 4px rgba(0,0,0,0.4); cursor: pointer;
      `;
      el.title = `${p.name}${p.maxPowerKw ? ` — ${p.maxPowerKw} kW` : ''}`;
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        setSelected(p);
      });
      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([p.longitude, p.latitude])
        .addTo(mapRef.current!);
      markersRef.current.push(marker);
    }
  }, [points]);

  const stats = useMemo(() => {
    const fast = points.filter((p) => (p.maxPowerKw ?? 0) >= 50).length;
    const hpc = points.filter((p) => (p.maxPowerKw ?? 0) >= 150).length;
    return { total: points.length, fast, hpc };
  }, [points]);

  if (!hasEvModule) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-800">
          <h2 className="font-semibold mb-1">{t('ev_chargers.module_off_title')}</h2>
          <p className="text-sm">
            {t('ev_chargers.module_off_body')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white">
      {/* Toolbar */}
      <div className="border-b border-gray-200 bg-white px-4 py-3 flex items-center gap-4 flex-wrap">
        <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <BatteryCharging className="h-5 w-5 text-emerald-600" />
          {t('ev_chargers.title')}
        </h1>

        {/* Address search */}
        <div className="relative w-full sm:flex-1 sm:min-w-[220px] sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setSearchOpen(false);
              if (e.key === 'Enter' && searchResults[0]) {
                e.preventDefault();
                selectSearchResult(searchResults[0]);
              }
            }}
            placeholder={t('ev_chargers.search_placeholder')}
            className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded text-sm focus:ring-emerald-500 focus:border-emerald-500"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
              aria-label={t('ev_chargers.clear_search')}
            >
              ×
            </button>
          )}
          {searchOpen && (searching || searchResults.length > 0) && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-20 max-h-72 overflow-y-auto">
              {searching && (
                <div className="px-3 py-2 text-xs text-gray-500">{t('ev_chargers.searching')}</div>
              )}
              {!searching && searchResults.length === 0 && (
                <div className="px-3 py-2 text-xs text-gray-500">{t('ev_chargers.no_result')}</div>
              )}
              {!searching &&
                searchResults.map((r, i) => (
                  <button
                    key={`${r.latitude}-${r.longitude}-${i}`}
                    type="button"
                    onClick={() => selectSearchResult(r)}
                    className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-gray-900 truncate">{r.displayName}</div>
                        {r.type && (
                          <div className="text-xs text-gray-400 capitalize">{r.type}</div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 text-sm w-full sm:w-auto">
          <Filter className="h-4 w-4 text-gray-400 shrink-0" />
          <select
            value={minPowerKw}
            onChange={(e) => setMinPowerKw(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 flex-1 min-w-0 sm:flex-none"
          >
            {POWER_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {t('ev_chargers.power_label', { label: lbl(p.label) })}
              </option>
            ))}
          </select>
          <select
            value={connectionTypeId}
            onChange={(e) => setConnectionTypeId(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 flex-1 min-w-0 sm:flex-none"
          >
            {CONNECTION_TYPES.map((c) => (
              <option key={c.id} value={c.id}>
                {t('ev_chargers.plug_label', { label: lbl(c.label) })}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-3 text-xs text-gray-600">
          {loading && <span>{t('ev_chargers.loading')}</span>}
          {error && <span className="text-red-600">{error}</span>}
          <span>
            <strong className="text-gray-900">{stats.total}</strong> {t('ev_chargers.stations_count')}
            {stats.total > 0 && (
              <>
                {' · '}
                <span style={{ color: powerColor(50) }}>{stats.fast}</span> {t('ev_chargers.fast')}
                {' · '}
                <span style={{ color: powerColor(150) }}>{stats.hpc}</span> {t('ev_chargers.hpc')}
              </>
            )}
          </span>
        </div>
      </div>

      {/* Map — explicit absolute height: relying on flex-1 inside Layout's
          padded <main> gave MapLibre an unstable height at init time and the
          tile layer never painted. A direct calc/minmax sidesteps that. */}
      <div
        className="relative"
        style={{ height: 'calc(100vh - 220px)', minHeight: 500 }}
      >
        <div
          ref={containerRef}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        />

        {/* Legend */}
        <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur rounded shadow border border-gray-200 px-3 py-2 text-xs">
          <div className="font-semibold text-gray-700 mb-1">{t('ev_chargers.max_power')}</div>
          {[
            { color: powerColor(150), label: t('ev_chargers.power_hpc') },
            { color: powerColor(50), label: t('ev_chargers.power_dc') },
            { color: powerColor(22), label: t('ev_chargers.power_22') },
            { color: powerColor(7), label: t('ev_chargers.power_7') },
            { color: powerColor(null), label: t('ev_chargers.power_unknown') },
          ].map((row) => (
            <div key={row.label} className="flex items-center gap-2">
              <span
                className="inline-block w-3 h-3 rounded-full border border-white"
                style={{ background: row.color }}
              />
              {row.label}
            </div>
          ))}
        </div>

        {/* Selected POI panel */}
        {selected && (
          <PoiPanel
            poi={selected}
            isMobile={isMobile}
            onClose={() => setSelected(null)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Selected charge-point panel — floating draggable card. Same behavior on
 * mobile and desktop: tap a marker, drag the card by its header to reposition,
 * tap × to close.
 */
function PoiPanel({
  poi,
  isMobile,
  onClose,
}: {
  poi: EvChargePoint;
  isMobile: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const dragStartRef = useRef<{ x: number; y: number; top: number; right: number } | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Reset position when the selected POI changes
  useEffect(() => {
    setPos(null);
    dragStartRef.current = null;
  }, [poi.id]);

  function onHandlePointerDown(e: React.PointerEvent) {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const cur = pos ?? { top: 12, right: 12 };
    dragStartRef.current = { x: e.clientX, y: e.clientY, top: cur.top, right: cur.right };
  }

  function onHandlePointerMove(e: React.PointerEvent) {
    if (!dragStartRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setPos({
      top: Math.max(0, dragStartRef.current.top + dy),
      right: Math.max(0, dragStartRef.current.right - dx),
    });
  }

  function onHandlePointerUp() {
    dragStartRef.current = null;
  }

  // Body content (shared between mobile + desktop)
  const body = (
    <div className="px-4 py-3 space-y-2 text-sm">
      {(poi.address || poi.city) && (
        <div className="text-gray-600">
          {poi.address}
          {poi.address && poi.city ? ', ' : ''}
          {poi.postcode} {poi.city}
        </div>
      )}
      {poi.maxPowerKw !== null && (
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4" style={{ color: powerColor(poi.maxPowerKw) }} />
          <span className="font-medium">{t('ev_chargers.max_kw', { kw: poi.maxPowerKw })}</span>
          {poi.numberOfPoints && (
            <span className="text-gray-500">· {t('ev_chargers.points_count', { count: poi.numberOfPoints })}</span>
          )}
        </div>
      )}
      {poi.connections.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('ev_chargers.plugs_label')}</div>
          <div className="space-y-0.5">
            {poi.connections.map((c, i) => (
              <div key={`${c.typeId}-${i}`} className="flex items-center gap-2 text-xs">
                <Plug className="h-3 w-3 text-gray-400" />
                <span>{c.typeName ?? `Type ${c.typeId}`}</span>
                {c.powerKw !== null && <span className="text-gray-500">{c.powerKw} kW</span>}
                {c.quantity ? <span className="text-gray-400">×{c.quantity}</span> : null}
              </div>
            ))}
          </div>
        </div>
      )}
      {poi.status && <div className="text-xs text-gray-500">{t('ev_chargers.state_label', { status: poi.status })}</div>}
      {isMobile ? (
        <a
          className="mt-1 inline-flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-[#33ccff]/10 border border-[#33ccff]/30 text-[#0099cc] text-sm font-semibold active:scale-[0.97] transition-transform"
          href={`https://waze.com/ul?ll=${poi.latitude},${poi.longitude}&navigate=yes`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <MapPin className="w-4 h-4" />
          {t('ev_chargers.waze_route')}
        </a>
      ) : (
        <a
          className="block text-xs text-emerald-700 hover:underline pt-1"
          href={`https://www.google.com/maps/dir/?api=1&destination=${poi.latitude},${poi.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('ev_chargers.google_maps_route')}
        </a>
      )}
    </div>
  );

  // Floating draggable card — same on mobile and desktop. On mobile we cap
  // the width to avoid overflow; on desktop the fixed 320px (w-80) wins.
  const top = pos?.top ?? 12;
  const right = pos?.right ?? 12;
  return (
    <div
      ref={containerRef}
      className="absolute bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-30"
      style={{
        top,
        right,
        width: 'min(320px, calc(100vw - 24px))',
        maxHeight: 'calc(100% - 24px)',
        overflowY: 'auto',
        touchAction: 'none',
      }}
    >
      <div
        className="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-3 cursor-grab active:cursor-grabbing select-none"
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={onHandlePointerUp}
        onPointerCancel={onHandlePointerUp}
      >
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 flex items-center gap-2">
            <MapPin className="h-4 w-4 text-emerald-600 shrink-0" />
            <span className="truncate">{poi.name}</span>
          </div>
          {poi.operator && (
            <div className="text-xs text-gray-500 mt-0.5 truncate">{poi.operator}</div>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label={t('ev_chargers.close')}
        >
          ×
        </button>
      </div>
      {body}
    </div>
  );
}
